import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const KungPaoChicken: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Authentic Kung Pao Chicken Recipe | Chinese Food Maps</title>
        <meta name="description" content="Master the art of making authentic Sichuan Kung Pao Chicken with our detailed recipe. Learn the secrets of balancing spicy, sweet, and savory flavors in this classic dish." />
        <meta property="og:title" content="Authentic Kung Pao Chicken Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Master the art of making authentic Sichuan Kung Pao Chicken with our detailed recipe." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/kung-pao-chicken" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/kung-pao-chicken" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Authentic Kung Pao Chicken Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Kung Pao Chicken (宫保鸡丁) is a classic Sichuan dish known for its perfect balance of spicy, sweet, sour, and savory flavors, combined with the unique numbing sensation of Sichuan peppercorns.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Chicken Marinade:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 lb chicken thighs, cut into 1-inch cubes</li>
              <li>1 tbsp light soy sauce</li>
              <li>1 tbsp Shaoxing wine</li>
              <li>1 tsp cornstarch</li>
              <li>¼ tsp white pepper</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Sauce:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>2 tbsp light soy sauce</li>
              <li>1 tbsp dark soy sauce</li>
              <li>1 tbsp black vinegar</li>
              <li>2 tbsp sugar</li>
              <li>1 tsp sesame oil</li>
              <li>2 tsp cornstarch</li>
              <li>3 tbsp water</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Additional Ingredients:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>½ cup roasted peanuts</li>
              <li>8-10 dried red chilies, cut into 1-inch pieces</li>
              <li>2 tsp Sichuan peppercorns</li>
              <li>4 cloves garlic, minced</li>
              <li>1-inch ginger, minced</li>
              <li>4 scallions, cut into 1-inch pieces</li>
              <li>2 tbsp vegetable oil</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Marinate the Chicken:</strong> Mix chicken with marinade ingredients. Let sit for 30 minutes.
              </li>
              <li>
                <strong>Prepare the Sauce:</strong> Combine all sauce ingredients in a bowl. Set aside.
              </li>
              <li>
                <strong>Heat the Wok:</strong> Get your wok smoking hot. Add oil and stir-fry chicken until golden. Remove and set aside.
              </li>
              <li>
                <strong>Aromatics:</strong> In the same wok, fry chilies and Sichuan peppercorns until fragrant. Add garlic and ginger.
              </li>
              <li>
                <strong>Combine:</strong> Return chicken to wok. Add sauce, peanuts, and scallions. Stir-fry until sauce thickens.
              </li>
              <li>
                <strong>Serve:</strong> Garnish with extra scallions and serve hot with steamed rice.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Toast the peanuts before using for extra flavor</li>
                <li>Don't burn the chilies - they should be fragrant but not black</li>
                <li>Use chicken thighs instead of breast for more tender meat</li>
                <li>Adjust the number of chilies to your preferred spice level</li>
                <li>Have all ingredients ready before heating the wok - this dish cooks quickly!</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};